import {div, hbox, span} from '@xh/hoist/cmp/layout';
import {fmtNumber} from '@xh/hoist/format';
import classNames from 'classnames';
import {createElement, ReactNode} from 'react';
import './MiniCharts.scss';

/**
 * Small inline charts built from plain divs and SVG, cheap enough to re-render on every live tick.
 * They take their size from CSS, so the same chart fits a grid cell or a summary panel.
 */

/** Cell holding a drawn chart - narrow side padding, so the chart gets most of the column. */
export const MINI_CHART_CELL = 'rzx-mini-chart-cell';

/**
 * Price path as an SVG line over a faint area, with a dashed reference line at the first point
 * (the prior close). Colored by the move from first to last point. The SVG stretches to the size
 * its CSS gives it, and strokes keep their width while it does.
 *
 * With `slots`, the x axis spans that many points, so a series still filling in (today's, during the
 * session) covers only the part of the width it has reached.
 */
export function sparkline(history: number[], className: string = null, slots: number = null): ReactNode {
    if (!history || history.length < 2) return null;

    let lo = history[0],
        hi = history[0];
    history.forEach(v => {
        if (v < lo) lo = v;
        if (v > hi) hi = v;
    });

    const range = hi - lo || 1,
        prevClose = history[0],
        change = history[history.length - 1] - prevClose,
        dir = change > 0 ? 'up' : change < 0 ? 'down' : 'flat',
        span = Math.max(slots ?? 0, history.length) - 1,
        x = (i: number) => (i / span) * 100,
        y = (v: number) => 2 + (1 - (v - lo) / range) * 36,
        line = history.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');

    return createElement(
        'svg',
        {className: classNames('rzx-spark', `rzx-spark--${dir}`, className), viewBox: '0 0 100 40', preserveAspectRatio: 'none'},
        createElement('polygon', {className: 'rzx-spark__area', points: `0,40 ${line} ${x(history.length - 1).toFixed(1)},40`}),
        createElement('line', {className: 'rzx-spark__ref', x1: 0, x2: 100, y1: y(prevClose), y2: y(prevClose)}),
        createElement('polyline', {className: 'rzx-spark__line', points: line})
    );
}

/**
 * Low-high range on a single line: low label, a track with a marker where the value sits, high label.
 * Stays one text line tall, so it fits any grid row height.
 */
export function rangeBar(low: number, high: number, value: number): ReactNode {
    if (low == null || high == null || value == null) return null;

    const width = high - low,
        pos = width > 0 ? Math.min(Math.max((value - low) / width, 0), 1) : 0.5;

    return hbox({
        className: 'rzx-range-bar',
        items: [
            span({className: 'rzx-range-bar__label', item: fmtNumber(low, {precision: 2}) as string}),
            div({
                className: 'rzx-range-bar__track',
                items: [
                    div({className: 'rzx-range-bar__fill', style: {width: `${pos * 100}%`}}),
                    div({className: 'rzx-range-bar__marker', style: {left: `${pos * 100}%`}})
                ]
            }),
            span({className: 'rzx-range-bar__label', item: fmtNumber(high, {precision: 2}) as string})
        ]
    });
}

/**
 * Share of a whole as a one-line bar with the percent beside it. The fill is red for a negative value (a short book).
 * Stays one text line tall, so it fits any grid row height.
 */
export function shareBar(value: number, whole: number): ReactNode {
    if (value == null || !whole) return null;

    const share = Math.min(Math.abs(value) / Math.abs(whole), 1);
    return hbox({
        className: classNames('rzx-share-bar', value < 0 && 'rzx-share-bar--neg'),
        items: [
            div({className: 'rzx-share-bar__track', item: div({className: 'rzx-share-bar__fill', style: {width: `${share * 100}%`}})}),
            span({className: 'rzx-share-bar__label', item: `${(share * 100).toFixed(1)}%`})
        ]
    });
}
